import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'helpers/api';
import { Alert } from 'react-bootstrap';
import { FaClock, FaPrescription } from 'react-icons/fa';

const OrderLogs = ({ data, orderId, logs: initialLogs }) => {
    const location = useLocation();
    const state = location?.state?.original || location?.state?.data;
    const currentOrderId = orderId || data?.orderid || data?.id || state?.orderid || state?.id;

    const [logs, setLogs] = useState(null);
    const [loading, setLoading] = useState(true);
    const [theme, setTheme] = useState(document.documentElement.getAttribute('data-bs-theme') || 'light');

    useEffect(() => {
        const observer = new MutationObserver(() => {
            const current = document.documentElement.getAttribute('data-bs-theme') || 'light';
            setTheme(current);
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-bs-theme']
        });

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const fetchOrderLogs = async () => {
            if (!currentOrderId) {
                setLogs(initialLogs || {});
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                const tokenStr = localStorage.getItem("accessToken");
                const payload = {
                    order_ids: [Number(currentOrderId)]
                };
                const response = await axios.post(
                    `${process.env.REACT_APP_SERVER_URL}/orders/order-logs/`,
                    payload,
                    { headers: { Authorization: `Bearer ${tokenStr}` } }
                );

                const resData = response.data;
                let logsData = null;

                if (resData?.logs) {
                    logsData = resData.logs;
                } else if (resData?.data?.logs) {
                    logsData = resData.data.logs;
                } else if (resData?.order_logs) {
                    logsData = resData.order_logs;
                } else if (resData?.order_status_log) {
                    logsData = resData.order_status_log;
                } else if (resData?.[currentOrderId]) {
                    logsData = resData[currentOrderId];
                } else if (resData?.data?.[currentOrderId]) {
                    logsData = resData.data[currentOrderId];
                } else if (resData?.data && typeof resData.data === 'object') {
                    logsData = resData.data;
                } else if (typeof resData === 'object') {
                    logsData = resData;
                }

                if (logsData && logsData[currentOrderId] && typeof logsData[currentOrderId] === 'object') {
                    logsData = logsData[currentOrderId];
                }

                setLogs(logsData || {});
            } catch (err) {
                console.error("Error fetching order logs:", err);
                if (initialLogs) {
                    setLogs(initialLogs);
                } else {
                    setLogs({});
                }
            } finally {
                setLoading(false);
            }
        };

        fetchOrderLogs();
    }, [currentOrderId]);

    const groupedByDate = (logs && typeof logs === 'object' && !Array.isArray(logs))
        ? Object.entries(logs)
            .map(([date, timeGroups]) => ({
                date,
                times: (timeGroups && typeof timeGroups === 'object' && !Array.isArray(timeGroups))
                    ? Object.entries(timeGroups)
                        .map(([time, entries]) => ({
                            time,
                            entries: Array.isArray(entries) ? entries : [entries]
                        }))
                        .sort((a, b) => new Date(`${date} ${b.time}`) - new Date(`${date} ${a.time}`))
                    : Array.isArray(timeGroups)
                        ? [{ time: '', entries: timeGroups }]
                        : [],
            }))
            .sort((a, b) => new Date(b.date) - new Date(a.date))
        : [];

    const hasLogs = groupedByDate.length > 0;
    const shimmerClass = theme === 'dark' ? 'order-logs-shimmer-dark' : 'order-logs-shimmer';

    if (loading) {
        return (
            <div className="timeline p-2">
                <style>{`
                    @keyframes orderLogsShimmer {
                        0% { background-position: -400px 0; }
                        100% { background-position: 400px 0; }
                    }
                    .order-logs-shimmer {
                        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                        background-size: 800px 100%;
                        animation: orderLogsShimmer 1.5s infinite linear;
                        border-radius: 4px;
                    }
                    .order-logs-shimmer-dark {
                        background: linear-gradient(90deg, #2e2e2e 25%, #444444 50%, #2e2e2e 75%);
                        background-size: 800px 100%;
                        animation: orderLogsShimmer 1.5s infinite linear;
                        border-radius: 4px;
                    }
                `}</style>
                {[1, 2].map((group) => (
                    <div key={group} className="timeline-date-group mb-4">
                        <div className="mb-3">
                            <div className={shimmerClass} style={{ height: '22px', width: '140px' }} />
                        </div>
                        {[1, 2].map((item) => (
                            <div key={item} className="timeline-item mb-3">
                                <div className="timeline-content-new p-3 border rounded">
                                    <div className="d-flex align-items-center gap-2 mb-3">
                                        <div className={`${shimmerClass} rounded-circle`} style={{ width: '16px', height: '16px' }} />
                                        <div className={shimmerClass} style={{ height: '14px', width: '90px' }} />
                                    </div>
                                    <div className="ps-3 border-start border-2 border-300">
                                        <div className="mb-2">
                                            <div className={`${shimmerClass} mb-2`} style={{ height: '14px', width: '65%' }} />
                                            <div className={shimmerClass} style={{ height: '12px', width: '45%' }} />
                                        </div>
                                    </div>
                                    <div className="d-flex justify-content-end mt-2">
                                        <div className={shimmerClass} style={{ height: '12px', width: '80px' }} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="timeline p-2">
            {hasLogs ? (
                groupedByDate.map(({ date, times }) => (
                    <div key={date} className="timeline-date-group">
                        <div className="timeline-date">
                            {new Date(date).toDateString()}
                        </div>

                        {times.map(({ time, entries }) => (
                            <div key={time} className="timeline-item">
                                {/* <div className="timeline-icon my-2 mr-2">
                                    <FaPrescription />
                                </div> */}
                                <div className="timeline-content-new">
                                    <div className="timeline-title d-flex align-items-center gap-2 mb-2">
                                        <FaClock className="text-muted" />
                                        <span>
                                            {new Date(`${date} ${time}`).toLocaleString('en-US', {
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                hour12: true,
                                            })}
                                        </span>
                                    </div>
                                    <div className="ps-3 border-start border-2 border-300">
                                        {entries.map((entry, i) => (
                                            <div
                                                key={i}
                                                className={`${i < entries.length - 1 ? 'mb-1 border-bottom border-dashed' : ''}`}
                                            >
                                                <div className="fw-semibold text-800 mb-1">{entry.title}</div>
                                                {entry.old_value !== null &&
                                                    entry.old_value !== undefined &&
                                                    entry.old_value !== "" ? (
                                                    <div className="timeline-desc">
                                                        <span className="text-bold">{entry.field ?? entry.col_changed}</span>
                                                        <span> changed from <em>{entry.old_value}</em> to <em>{entry.new_value}</em></span>
                                                    </div>
                                                ) : entry.new_value ? (
                                                    <div className="timeline-desc">
                                                        <span className="text-bold">{entry.field ?? entry.col_changed}</span>
                                                        <span> has been set to <em>{entry.new_value}</em></span>
                                                    </div>
                                                ) : null}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="timeline-footer d-flex justify-content-end text-muted small mt-2">
                                        <span>By {entries[0]?.user}</span>
                                    </div>

                                </div>
                            </div>
                        ))}

                    </div>
                ))
            ) : (
                <Alert variant="info" className="text-center">
                    <strong>No Order Logs Available</strong>
                </Alert>
            )}
        </div>
    );
};

export default OrderLogs;